import { useContext, useEffect, useMemo, useState } from "react";

import { PatientsFilterContext } from "utils/contexts";

import type { Filters } from "./useFilters";

type WeekFilterData = {
  week: {
    active: boolean;
    from?: number;
    to?: number;
  };
};

export const useWeekFilter = () => {
  const { activeFilters, setFilter } = useContext<Filters>(PatientsFilterContext);

  const weekFilterValue = useMemo(() => {
    const weekFilter = activeFilters.find(f => typeof f === "object" && f !== null && "week" in f);
    if (weekFilter && typeof weekFilter === "object" && "week" in weekFilter) {
      const weekData = (weekFilter as WeekFilterData).week;
      return [weekData.from ?? 0, weekData.to ?? 100];
    }
    return [0, 100];
  }, [activeFilters]);

  const [fromInput, setFromInput] = useState<string>(String(weekFilterValue[0]));
  const [toInput, setToInput] = useState<string>(String(weekFilterValue[1]));

  useEffect(() => {
    if (!activeFilters.includes("week")) {
      setFromInput("0");
      setToInput("100");
    }
  }, [activeFilters]);

  const handleWeekInput = (isFrom: boolean) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (isFrom) {
      setFromInput(value);
    } else {
      setToInput(value);
    }

    const numValue = parseInt(value, 10);
    if (!Number.isNaN(numValue) && numValue >= 0) {
      const fromValue = parseInt(fromInput, 10);
      const toValue = parseInt(toInput, 10);

      let finalFrom: number;
      let finalTo: number;

      if (isFrom) {
        finalFrom = numValue;
        if (Number.isNaN(toValue)) {
          finalTo = 100;
        } else {
          finalTo = toValue;
        }
      } else {
        if (Number.isNaN(fromValue)) {
          finalFrom = 0;
        } else {
          finalFrom = fromValue;
        }
        finalTo = numValue;
      }

      setFilter("week", {
        active: true,
        from: finalFrom,
        to: finalTo,
      });
    }
  };

  return {
    fromInput,
    toInput,
    handleWeekInput,
  };
};
